import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  PASSPORT_INSTRUCTION_COUNT,
  PASSPORT_PHOTO_INSTRUCTIONS,
} from "../config/passportPhotoInstructions";
import PassportInstructionIllustration from "./PassportInstructionIllustration";
import "./PassportPhotoInstructionsPreview.css";

/** Fixed instruction card dimensions (px) — base +15%. */
export const INSTRUCTION_CARD_WIDTH = 552;
export const INSTRUCTION_CARD_HEIGHT = 368;
export const INSTRUCTION_PHOTO_SIZE = 173;

const SLIDE_DURATION_MS = 10000;

/**
 * Pre-upload instruction carousel on the right canvas.
 * Replaced by the uploaded image once the user adds a photo.
 */
const PassportPhotoInstructionsPreview = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  const slideStartRef = useRef(Date.now());
  const pausedAccumRef = useRef(0);
  const pauseStartedRef = useRef(null);
  const slideIndexRef = useRef(slideIndex);

  slideIndexRef.current = slideIndex;

  const slide = PASSPORT_PHOTO_INSTRUCTIONS[slideIndex];
  const total = PASSPORT_INSTRUCTION_COUNT;

  const resetTimer = useCallback(() => {
    slideStartRef.current = Date.now();
    pausedAccumRef.current = 0;
    pauseStartedRef.current = null;
    setProgress(0);
  }, []);

  const goToSlide = useCallback(
    (index) => {
      setSlideIndex(index);
      resetTimer();
    },
    [resetTimer]
  );

  const goPrev = useCallback(() => {
    goToSlide((slideIndex - 1 + total) % total);
  }, [goToSlide, slideIndex, total]);

  const goNext = useCallback(() => {
    goToSlide((slideIndex + 1) % total);
  }, [goToSlide, slideIndex, total]);

  const handlePause = useCallback(() => {
    if (pauseStartedRef.current != null) return;
    pauseStartedRef.current = Date.now();
    setPaused(true);
  }, []);

  const handleResume = useCallback(() => {
    if (pauseStartedRef.current == null) return;
    pausedAccumRef.current += Date.now() - pauseStartedRef.current;
    pauseStartedRef.current = null;
    setPaused(false);
  }, []);

  useEffect(() => {
    if (paused) return undefined;

    let raf = 0;
    const tick = () => {
      const elapsed =
        Date.now() - slideStartRef.current - pausedAccumRef.current;
      const nextProgress = Math.min(elapsed / SLIDE_DURATION_MS, 1);
      setProgress(nextProgress);

      if (nextProgress >= 1) {
        const next = (slideIndexRef.current + 1) % total;
        slideStartRef.current = Date.now();
        pausedAccumRef.current = 0;
        setProgress(0);
        setSlideIndex(next);
        raf = requestAnimationFrame(tick);
        return;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, total]);

  const pillFill = (index) => {
    if (index < slideIndex) return 100;
    if (index > slideIndex) return 0;
    return progress * 100;
  };

  return (
    <div className="appm-instr-preview">
      <p className="appm-instr-preview__eyebrow">Instructions:</p>
      <div
        className="appm-instr-preview__card"
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
      >
        <h2 className="appm-instr-preview__heading">
          How to take a good photo?
        </h2>

        <div className="appm-instr-preview__carousel">
          <button
            type="button"
            className="appm-instr-preview__nav"
            aria-label="Previous instruction"
            onClick={goPrev}
          >
            ‹
          </button>

          <div className="appm-instr-preview__compare">
            <div className="appm-instr-preview__photo">
              <PassportInstructionIllustration variant={slide.goodVariant} />
              <span
                className="appm-instr-preview__badge appm-instr-preview__badge--good"
                aria-label="Good example"
              >
                ✓
              </span>
            </div>
            <div className="appm-instr-preview__photo">
              <PassportInstructionIllustration variant={slide.badVariant} />
              <span
                className="appm-instr-preview__badge appm-instr-preview__badge--bad"
                aria-label="Bad example"
              >
                ✕
              </span>
            </div>
          </div>

          <button
            type="button"
            className="appm-instr-preview__nav"
            aria-label="Next instruction"
            onClick={goNext}
          >
            ›
          </button>
        </div>

        <p className="appm-instr-preview__step">
          <span className="appm-instr-preview__step-count">
            {slideIndex + 1}/{total}
          </span>{" "}
          {slide.title}
        </p>

        <p className="appm-instr-preview__message">{slide.message}</p>

        <div
          className="appm-instr-preview__pills"
          role="tablist"
          aria-label="Instruction steps"
        >
          {PASSPORT_PHOTO_INSTRUCTIONS.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={i === slideIndex}
              aria-label={`Step ${i + 1}: ${item.title}`}
              className={`appm-instr-preview__pill${
                i === slideIndex ? " appm-instr-preview__pill--active" : ""
              }${i < slideIndex ? " appm-instr-preview__pill--done" : ""}`}
              onClick={() => goToSlide(i)}
            >
              <span
                className="appm-instr-preview__pill-fill"
                style={{ width: `${pillFill(i)}%` }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PassportPhotoInstructionsPreview;
